/// <reference types="@angular/localize" />

import { Component, computed, DestroyRef, effect, inject, input, OnInit, signal } from '@angular/core';
import { DmButton, DmCheckbox, DmPinInput } from '@digitale-menschen/component-library-angular';
import { lastValueFrom } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import {
  authenticationControllerSendOtpAgainV1,
  authenticationControllerVerifyOtpV1,
  getLexplusVerifyOtpDtoInitialValue,
  lexplusVerifyOtpDtoSchema,
} from '@lexplus-authentication-api';
import { API_CONFIGURATION } from '@lexplus-environments';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService } from '../../services/authentication.service';
import { Store } from '@ngxs/store';
import {
  fieldErrorMessage,
  FormAlertComponent,
  FormAlertVariant,
  LocalizedRouteService,
  ProfileState,
  TemporarySessionState,
} from '@lexplus-shared-lib';
import { form, FormField, FormRoot } from '@angular/forms/signals';
import { AUTH_ROUTE_IDS, LOCALIZED_AUTH_ROUTES } from '../../auth-route-const';

@Component({
  selector: 'lp-two-fa-form',
  imports: [DmButton, DmCheckbox, RouterLink, FormField, FormRoot, DmPinInput, FormAlertComponent],
  templateUrl: './two-fa-form.component.html',
  styleUrl: './two-fa-form.component.scss',
})
export class LexplusTwoFaFormComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly localizedRouteService = inject(LocalizedRouteService);

  public readonly isForAdmin = input<boolean>(false);

  private readonly codeModel = signal(getLexplusVerifyOtpDtoInitialValue());
  protected readonly codeForm = form(this.codeModel, lexplusVerifyOtpDtoSchema, {
    submission: { action: () => this.confirm2Fa() },
  });

  protected readonly loadingStatus = signal<'loading' | 'success' | 'error' | null>(null);
  /** Verification or resend failure, shown inside the form instead of as a toast. */
  private readonly formErrorMessage = signal<string | null>(null);
  /** Resend confirmation, shown in the same slot as the failure so one button reports in one place. */
  private readonly formSuccessMessage = signal<string | null>(null);

  protected readonly otpErrorMessage = computed(() =>
    fieldErrorMessage(this.codeForm.otp(), $localize`:@@auth.twoFa.codeRequired:Code ist erforderlich.`),
  );
  /** One alert slot for both outcomes, so the resend button never reports in two places. */
  protected readonly alertMessage = computed(() => this.formErrorMessage() ?? this.formSuccessMessage());
  protected readonly alertVariant = computed<FormAlertVariant>(() => (this.formErrorMessage() ? 'error' : 'success'));
  /** The success here is always a resent code, which reads better as a send icon than a tick. */
  protected readonly alertIcon = computed(() => (this.formErrorMessage() ? null : this.resendIcon));

  /** Editing the code retracts the previous failure, so the alert never contradicts the input. */
  private readonly clearErrorOnCodeEditEffect = effect(() => {
    this.codeForm.otp().value();
    this.formErrorMessage.set(null);
  });

  /** The generated schema only marks the code as required, so the length is checked here. */
  protected readonly OTP_LENGTH = 6;

  /** Registered by both apps in `provideDmIcons`; not part of the library's default set. */
  private readonly resendIcon = 'send-01';

  /** How long a resend confirmation stays before it becomes stale noise. */
  private readonly successMessageDurationMs = 5000;

  protected loginRoute = '';
  private successMessageTimer?: ReturnType<typeof setTimeout>;
  private email: string | undefined;
  private userId: string | undefined;

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.successMessageTimer));
  }

  public ngOnInit(): void {
    this.loginRoute =
      '/' +
      this.localizedRouteService.getLocalizedRoute(LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.ROOT], LOCALIZED_AUTH_ROUTES[AUTH_ROUTE_IDS.LOGIN]);
    this.email = this.store.selectSnapshot(TemporarySessionState.userEmail);
    this.userId = this.store.selectSnapshot(TemporarySessionState.userId);
    if (!this.email || !this.userId) {
      void this.router.navigateByUrl(this.loginRoute);
      return;
    }

    this.codeForm.userId().value.set(<string>this.userId);
    this.codeForm.email().value.set(<string>this.email);
    this.codeForm.appDeviceId?.().value.set(this.store.selectSnapshot(ProfileState.appDeviceId) || '');
  }

  protected async sendOtpAgain(): Promise<void> {
    this.formErrorMessage.set(null);
    this.clearResendConfirmation();

    try {
      const result = await lastValueFrom(
        authenticationControllerSendOtpAgainV1(this.httpClient, this.apiConfiguration.baseUrlAuth, {
          body: { email: this.codeForm.email().value(), userId: this.codeForm.userId().value() },
        }),
      );
      if (result && result.body.success) {
        // Only what the click achieved — the heading and lead above already say to check
        // the inbox, so repeating that tells the user nothing they did not just read.
        this.showResendConfirmation($localize`:@@auth.twoFa.otpResent:Code erneut gesendet.`);
      }
    } catch (error) {
      const serverMessage = (error as HttpErrorResponse)?.error?.message;
      this.formErrorMessage.set(serverMessage ?? $localize`:@@auth.twoFa.otpSentFailedDescription:Bitte in einem Moment erneut versuchen.`);
    }
  }

  /**
   * Confirms the two-factor authentication (2FA) and redirect
   */
  private async confirm2Fa(): Promise<void> {
    this.loadingStatus.set('loading');
    // Cleared up front so a repeated failure re-mounts the alert, which both
    // re-announces it and makes the retry visible.
    this.formErrorMessage.set(null);
    this.clearResendConfirmation();

    try {
      await lastValueFrom(
        authenticationControllerVerifyOtpV1(this.httpClient, this.apiConfiguration.baseUrlAuth, {
          body: this.codeForm().value(),
        }),
      );

      const mustChangePassword = this.store.selectSnapshot(TemporarySessionState.mustChangePassword);
      const passwordChangeToken = this.store.selectSnapshot(TemporarySessionState.passwordChangeToken);
      const isFirstLogin = this.store.selectSnapshot(TemporarySessionState.isFirstLogin);

      await this.authenticationService.routeAndProcessAfterLogin(mustChangePassword, isFirstLogin, passwordChangeToken, <string>this.email);
    } catch (twoFaFailedException) {
      this.loadingStatus.set('error');
      // Prefer the server message (e.g. the rate limit «Zu viele Fehlversuche …»), else fall back.
      const serverMessage = (twoFaFailedException as HttpErrorResponse)?.error?.message;
      this.formErrorMessage.set(
        serverMessage ?? $localize`:@@auth.twoFa.verificationFailedDescription:Der eingegebene Code ist ungültig. Bitte erneut versuchen.`,
      );
    }
  }

  /** Shows the confirmation and retires it, so a stale «code sent» cannot outlive its usefulness. */
  private showResendConfirmation(message: string): void {
    this.clearResendConfirmation();
    this.formSuccessMessage.set(message);
    this.successMessageTimer = setTimeout(() => this.formSuccessMessage.set(null), this.successMessageDurationMs);
  }

  private clearResendConfirmation(): void {
    clearTimeout(this.successMessageTimer);
    this.formSuccessMessage.set(null);
  }
}
